import React, { useState } from "react";
import { useNavigate, Navigate, useSearchParams } from "react-router-dom";
import { HandCoins, Lock, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { affiliateApi } from "../../lib/api";
import { formatApiErrorDetail } from "../../contexts/AuthContext";

export default function AffiliateLogin() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [checked, setChecked] = useState(false);
  const [alreadyIn, setAlreadyIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  React.useEffect(() => {
    const stripeCode = searchParams.get("code");
    if (stripeCode) {
      // Returning from the Stripe Connect OAuth flow — capture the code,
      // save it, then strip it from the URL so it's never resubmitted.
      affiliateApi.me()
        .then(() => affiliateApi.connectStripe(stripeCode))
        .then(() => {
          toast.success("Stripe account connected!");
          navigate("/affiliate/payouts", { replace: true });
        })
        .catch(() => {
          window.history.replaceState({}, "", "/affiliate/login");
          toast.error("Please log in to finish connecting Stripe.");
          setChecked(true);
        });
      return;
    }
    affiliateApi.me().then(() => setAlreadyIn(true)).catch(() => {}).finally(() => setChecked(true));
  }, [searchParams, navigate]);

  if (alreadyIn) return <Navigate to="/affiliate/dashboard" replace />;
  if (!checked) {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0E17]"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await affiliateApi.login(email.trim().toLowerCase(), password);
      navigate("/affiliate/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
      toast.error("Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0E17] px-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
        backgroundImage: "radial-gradient(circle at 1px 1px, #FBBF24 1px, transparent 0)", backgroundSize: "28px 28px",
      }} />
      <div className="absolute top-[-10%] left-1/2 -translate-x-1/2 h-72 w-[36rem] bg-amber-500/10 blur-[120px] rounded-full pointer-events-none" />

      <div className="relative w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-amber-500/20 mb-4">
            <HandCoins className="h-7 w-7 text-white" />
          </div>
          <div className="text-white font-bold text-[19px] tracking-tight">Affiliate Portal</div>
          <div className="text-[11px] text-slate-500 uppercase tracking-[0.2em] mt-1">ServiceGuru Pro · Partners</div>
        </div>

        <form onSubmit={onSubmit} className="space-y-4 bg-white/[0.03] border border-white/10 rounded-2xl p-6 backdrop-blur-sm" data-testid="affiliate-login-form">
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Email</label>
            <div className="relative">
              <Mail className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="email" required autoFocus value={email}
                data-testid="affiliate-login-email"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@partner.com"
                className="w-full h-11 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50"
              />
            </div>
          </div>
          <div>
            <label className="block text-[11.5px] font-semibold text-slate-400 mb-1.5 uppercase tracking-wider">Password</label>
            <div className="relative">
              <Lock className="h-4 w-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="password" required value={password}
                data-testid="affiliate-login-password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full h-11 pl-9 pr-3 rounded-lg bg-[#0F1623] border border-white/10 text-[13.5px] text-slate-100 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-500/50"
              />
            </div>
          </div>

          {error && (
            <div data-testid="affiliate-login-error" className="px-3 py-2 rounded-md bg-rose-500/10 border border-rose-500/30 text-[12.5px] text-rose-300">{error}</div>
          )}

          <button
            type="submit" disabled={submitting}
            data-testid="affiliate-login-submit"
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-300 hover:to-orange-400 text-[#0A0E17] text-[14px] font-bold disabled:opacity-60"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <HandCoins className="h-4 w-4" />}
            Sign in
          </button>
        </form>
        <div className="text-center text-[11px] text-slate-600 mt-6">Partner access · Contact the ServiceGuru Pro team for credentials</div>
      </div>
    </div>
  );
}
